import {
  ClientReportRecipient,
  ClientReportSchedule,
  Membership,
  ReportRecipientList,
} from "~/api/Api";

export type ScheduleFrequency = ClientReportSchedule["frequency"];

// Mirrors ClientReportSchedule's day validation: a monthly day stops at the
// 28th so every month has it, a weekly one runs from 1 Monday to 7 Sunday.
const WEEKDAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];
const MAX_MONTH_DAY = 28;

const ordinal = (day: number) =>
  day >= 11 && day <= 13
    ? `${day}th`
    : `${day}${["th", "st", "nd", "rd"][day % 10] ?? "th"}`;

export const dayLabel = (frequency: ScheduleFrequency, day: number) =>
  frequency === "monthly" ? ordinal(day) : (WEEKDAYS[day - 1] ?? String(day));

export const dayOptions = (frequency: ScheduleFrequency) =>
  Array.from(
    { length: frequency === "monthly" ? MAX_MONTH_DAY : WEEKDAYS.length },
    (_, index) => ({
      value: String(index + 1),
      label: dayLabel(frequency, index + 1),
    }),
  );

/** A day valid for the frequency: switching to weekly can't keep the 15th. */
export const dayFor = (frequency: ScheduleFrequency, day: number) =>
  day <= (frequency === "monthly" ? MAX_MONTH_DAY : WEEKDAYS.length) ? day : 1;

/** "Monthly on the 1st", "Weekly on Mondays". */
export const describeSchedule = ({
  frequency,
  day,
}: Pick<ClientReportSchedule, "frequency" | "day">) =>
  frequency === "monthly"
    ? `Monthly on the ${dayLabel(frequency, day)}`
    : `Weekly on ${dayLabel(frequency, day)}s`;

export const scheduleCoverage = (frequency: ScheduleFrequency) =>
  frequency === "monthly" ? "the previous month" : "the 7 days before";

// Mirrors ClientReportRecipient.with_access: a recipient who later loses
// access to the site keeps the row but isn't emailed.
export const hasSiteAccess = (membership: Membership, siteId: number) =>
  membership.admin ||
  !membership.site_ids?.length ||
  membership.site_ids.includes(siteId);

export const memberName = ({ id, user }: Membership) =>
  [user.first_name, user.last_name].filter(Boolean).join(" ") ||
  user.email ||
  `Member #${id}`;

const sentence = (items: string[]) =>
  items.length > 1
    ? `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`
    : (items[0] ?? "");

const upTo = (names: string[], limit: number) => {
  if (names.length <= limit) return names;

  const others = names.length - limit;

  return [
    ...names.slice(0, limit),
    `${others} ${others === 1 ? "other" : "others"}`,
  ];
};

/** "Ann", "Ann and Bob", "Ann, Bob and Cid", "Ann, Bob and 2 others". */
export const joinNames = (names: string[], limit = Infinity) =>
  sentence(upTo(names, limit));

/** "Ann and Bob", "Ann and the “Clients” list (2 addresses)". */
export const describeRecipients = (
  names: string[],
  listLabel?: string,
  limit = Infinity,
) => sentence([...upTo(names, limit), ...(listLabel ? [listLabel] : [])]);

const pluralize = (count: number, noun: string) =>
  `${count} ${count === 1 ? noun : `${noun}es`}`;

/**
 * How the site's recipient list reads in a sentence. Only admins can load
 * the team's lists, so members just learn that there is one.
 */
export const listLabel = (
  listId: number | null | undefined,
  list?: ReportRecipientList,
) => {
  if (listId == null) return undefined;
  if (!list) return "a recipient list";

  return `the “${list.name}” list (${pluralize(list.recipients.length, "address")})`;
};

// Mirrors ReportRecipientList: at most 50 recipients, each an address or a
// quoted name followed by the address in angle brackets.
export const MAX_LIST_RECIPIENTS = 50;
const NAMED = /^"([^"]*)"\s*<([^<>]*)>$/;
const UNQUOTED_NAME = /^([^"<>]+?)\s*<([^<>]+)>$/;

/** "Dana Scully" for `"Dana Scully" <dana@example.com>`, else the address. */
export const recipientName = (recipient: string) => {
  const match = NAMED.exec(recipient);

  return match?.[1].trim() || match?.[2].trim() || recipient;
};

/**
 * The addresses typed or pasted into a list, one per line or separated by
 * commas or semicolons outside quoted names. `Dana Scully <dana@…>`, as
 * mail apps copy it, gets the quotes the backend asks for.
 */
export const parseRecipients = (text: string) => {
  const entries: string[] = [];
  let current = "";
  let isQuoted = false;

  for (const char of text) {
    if (char === '"') isQuoted = !isQuoted;

    if (!isQuoted && /[\n\r,;]/.test(char)) {
      entries.push(current);
      current = "";
    } else {
      current += char;
    }
  }
  entries.push(current);

  return entries
    .map((entry) => entry.trim())
    .filter(Boolean)
    .map((entry) => {
      const match = UNQUOTED_NAME.exec(entry);

      return match ? `"${match[1]}" <${match[2].trim()}>` : entry;
    });
};

/** What to send to make `selected` (membership ids) the recipient list. */
export const recipientChanges = (
  current: ClientReportRecipient[],
  selected: number[],
) => ({
  add: selected.filter(
    (membershipId) =>
      !current.some((recipient) => recipient.membership_id === membershipId),
  ),
  remove: current
    .filter((recipient) => !selected.includes(recipient.membership_id))
    .map((recipient) => recipient.id),
});
